<!-- src/components/modals/CreateSchemeModal.vue -->
<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50" @click="close" />

        <!-- Modal -->
        <div class="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
          <!-- Header -->
          <div class="flex items-center justify-between p-5 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">Create Planning Scheme</h2>
            <button
              class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              @click="close"
            >
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>

          <!-- Form -->
          <form class="p-5 space-y-5" @submit.prevent="handleSubmit">
            <!-- Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                Scheme Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.name"
                type="text"
                required
                placeholder="e.g., Year 10 Biology"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-colors"
              />
            </div>

            <!-- Description -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5"> Description </label>
              <textarea
                v-model="form.description"
                rows="3"
                placeholder="Brief overview of this planning scheme..."
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none transition-colors"
              />
            </div>

            <!-- Course Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                Subject / Course
              </label>
              <input
                v-model="form.courseName"
                type="text"
                placeholder="e.g., Biology, Mathematics, History"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-colors"
              />
            </div>

            <!-- Color selection -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"> Color </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="colour in ASSIGNABLE_COLOURS"
                  :key="colour"
                  type="button"
                  class="w-10 h-10 rounded-lg transition-transform hover:scale-110"
                  :class="[form.colour === colour ? 'ring-2 ring-offset-2 ring-gray-900' : '']"
                  :style="{ backgroundColor: COLOUR_MAP[colour].hex }"
                  @click="form.colour = colour"
                />
              </div>
            </div>

            <!-- Error message -->
            <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p class="text-sm text-red-600">{{ error }}</p>
            </div>
          </form>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b-xl"
          >
            <AppButton
              variant="secondary"
              size="md"
              @click="close"
            >
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              size="md"
              :disabled="!form.name.trim()"
              :loading="isSubmitting"
              @click="handleSubmit"
            >
              Create Scheme
            </AppButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton } from '@/design-system/components'
import { planningApi } from '@/api'
import type { PlanningScheme } from '@/api'
import { ASSIGNABLE_COLOURS, COLOUR_MAP, type PaletteColour } from '@/constants/colors'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'created', scheme: PlanningScheme): void
}>()

const form = ref({
  name: '',
  description: '',
  courseName: '',
  colour: 'RED' as PaletteColour,
})

const isSubmitting = ref(false)
const error = ref<string | null>(null)

function close() {
  emit('update:modelValue', false)
}

async function handleSubmit() {
  if (!form.value.name.trim() || isSubmitting.value) return

  isSubmitting.value = true
  error.value = null

  try {
    const scheme = await planningApi.createScheme({
      name: form.value.name.trim(),
      description: form.value.description.trim() || undefined,
      colour: form.value.colour,
    })
    emit('created', scheme)
    close()
  } catch (err) {
    console.error('Failed to create scheme:', err)
    error.value = 'Failed to create planning scheme. Please try again.'
  } finally {
    isSubmitting.value = false
  }
}

// Reset form when modal opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      form.value = {
        name: '',
        description: '',
        courseName: '',
        colour: 'RED' as PaletteColour,
      }
      error.value = null
    }
  }
)
</script>
